import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronRight } from "lucide-react";
import BrandGlyph from "@/components/BrandGlyph";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    }
  };

  const openItem = (n) => {
    if (!n.link) return;
    setOpen(false);
    navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-lg hover:bg-white/10 transition-colors">
          <BrandGlyph name="bell" size={22} active />
          {unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-slate-900 border-white/10 text-white p-0" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-white/10 font-bold text-sm">Your inbox</div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-slate-500 text-sm">No messages yet.</p>}
          {items.map((n) => {
            const clickable = !!n.link;
            return (
              <div
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => openItem(n)}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openItem(n); } }}
                className={`px-4 py-3 border-b border-white/5 ${clickable ? "cursor-pointer hover:bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">C</span>
                  <p className="font-semibold text-sm text-white flex-1">{n.title}</p>
                  {clickable && <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                </div>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{n.body}</p>
                {clickable && <span className="text-[11px] font-semibold text-violet-300 mt-1.5 inline-block">Open →</span>}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
